/**
 * Fitment Garage — floating button + centered modal
 *
 * Storage:
 *   localStorage["ark.garage"]          = [vehicle, ...]
 *   localStorage["ark.selectedVehicle"] = currently active vehicle
 *
 * UX:
 *   - toggle click → modal open (focus to first focusable; trap Tab)
 *   - saved list: click radio/row → set selected + broadcast + close
 *   - saved list: × → remove
 *   - Add a vehicle form: cascade Year→Make→Model→Submodel(optional)
 *     → Save → add + set selected + close
 *   - close: × / backdrop / Escape → return focus to toggle
 */
(function () {
  'use strict';
  if (window.__arkGarageLoaded) return;
  window.__arkGarageLoaded = true;

  var GARAGE_KEY = 'ark.garage';
  var STORAGE_KEY = 'ark.selectedVehicle';
  var COOKIE_KEY = 'ark_vehicle';
  var COOKIE_MAX_AGE = 31536000;
  var VEHICLE_CHANGED_EVENT = 'ark:vehicle-changed';
  var VEHICLE_CHANGE_REQUEST_EVENT = 'ark:vehicle-change-request';
  var API_BASE = (typeof window !== 'undefined' && window.ARK_API_BASE) || '/apps/fitment';
  var SHOP_DOMAIN = typeof window !== 'undefined' ? window.ARK_SHOP_DOMAIN || null : null;
  var CASCADE_KEYS = ['year', 'make', 'model', 'submodel'];
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var DBG = function () {
    if (window.ARK_DEBUG !== false) {
      console.log.apply(console, ['[ark/garage]'].concat(Array.prototype.slice.call(arguments)));
    }
  };
  // make/model/submodel sanitize (英数 + 一部記号、最大 64 chars)
  var MAKE_MODEL_PATTERN = /^[A-Za-z0-9 \-\/.&]{1,64}$/;
  var YEAR_MIN = 1900;
  var YEAR_MAX = new Date().getFullYear() + 2;

  // ------- shared body scroll lock (Garage / other widgets で共有) --------
  if (!window.__arkScrollLock) {
    window.__arkScrollLock = {
      count: 0,
      prev: null,
      prevPad: null,
      lock: function () {
        if (this.count === 0) {
          // スクロールバーが占めていた幅を padding で埋め戻す。
          // これが無いと overflow:hidden にした瞬間にバーの幅ぶん
          // ページ全体が横にずれる (desktop で顕著)。
          var gap = window.innerWidth - document.documentElement.clientWidth;
          this.prev = document.body.style.overflow;
          this.prevPad = document.body.style.paddingRight;
          document.body.style.overflow = 'hidden';
          if (gap > 0) {
            var current = parseFloat(window.getComputedStyle(document.body).paddingRight);
            document.body.style.paddingRight = (isFinite(current) ? current : 0) + gap + 'px';
          }
        }
        this.count++;
      },
      unlock: function () {
        this.count = Math.max(0, this.count - 1);
        if (this.count === 0) {
          document.body.style.overflow = this.prev || '';
          document.body.style.paddingRight = this.prevPad || '';
          this.prev = null;
          this.prevPad = null;
        }
      },
    };
  }

  // ------- sanitize --------
  function isValidVehicle(v) {
    if (!v || typeof v !== 'object') return false;
    var year = typeof v.year === 'number' ? v.year : parseInt(v.year, 10);
    if (!isFinite(year) || year < YEAR_MIN || year > YEAR_MAX) return false;
    if (typeof v.make !== 'string' || !MAKE_MODEL_PATTERN.test(v.make)) return false;
    if (typeof v.model !== 'string' || !MAKE_MODEL_PATTERN.test(v.model)) return false;
    if (v.submodel && typeof v.submodel === 'string' && !MAKE_MODEL_PATTERN.test(v.submodel))
      return false;
    return true;
  }
  function sanitize(v) {
    if (!isValidVehicle(v)) return null;
    return {
      year: typeof v.year === 'number' ? v.year : parseInt(v.year, 10),
      make: v.make,
      model: v.model,
      submodel: v.submodel || '',
    };
  }
  function sanitizeArray(arr) {
    if (!Array.isArray(arr)) return [];
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      var s = sanitize(arr[i]);
      if (s) out.push(s);
    }
    return out;
  }

  // ------- storage --------
  function setCookie(name, value, maxAge) {
    try {
      document.cookie =
        name + '=' + encodeURIComponent(value) + '; path=/; max-age=' + maxAge + '; SameSite=Lax';
    } catch (_) {}
  }
  function loadGarage() {
    try {
      var raw = localStorage.getItem(GARAGE_KEY);
      return sanitizeArray(raw ? JSON.parse(raw) : []);
    } catch (_) {
      return [];
    }
  }
  function saveGarage(garage) {
    try {
      localStorage.setItem(GARAGE_KEY, JSON.stringify(garage));
    } catch (_) {}
    syncGarage();
  }

  /* ------- engage への記録専用ミラー -------
     localStorage が正本のまま。ここは「ログイン顧客が今どんな車両を入れているか」を
     管理画面で見るための控えで、読み戻さない (読み戻すと端末間同期になり、端末ごとに
     独立・ゲストでも使えるという既存の性質が変わる)。

     全置換で送るので、送信が 1 回落ちても次の変更で必ず追いつく。失敗は握る:
     ガレージ自体は localStorage で完結しており、同期の失敗で顧客の操作を止める理由がない。 */
  var SYNC_DEBOUNCE_MS = 800;
  var syncTimer = null;

  function syncConfig() {
    var c = window.ARK_GARAGE_SYNC;
    if (!c || !c.enabled || !c.loggedIn || !c.url) return null;
    return c;
  }

  function syncGarage() {
    if (!syncConfig()) return;
    // 連続操作 (車両を続けて消す等) を 1 回にまとめる。送るのは常に最新の全量。
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(pushGarage, SYNC_DEBOUNCE_MS);
  }

  function pushGarage() {
    syncTimer = null;
    var cfg = syncConfig();
    if (!cfg || typeof fetch !== 'function') return;
    try {
      fetch(cfg.url, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vehicles: loadGarage(), selected: loadSelected() }),
      }).catch(function () {});
    } catch (_) {}
  }
  function loadSelected() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? sanitize(JSON.parse(raw)) : null;
    } catch (_) {
      return null;
    }
  }
  function setSelected(vehicle) {
    var clean = sanitize(vehicle);
    if (!clean) return;
    try {
      var json = JSON.stringify(clean);
      localStorage.setItem(STORAGE_KEY, json);
      setCookie(COOKIE_KEY, json, COOKIE_MAX_AGE);
      window.dispatchEvent(new CustomEvent(VEHICLE_CHANGED_EVENT, { detail: clean }));
    } catch (_) {}
    // 選択の変更は台数が変わらないので saveGarage を通らない。ここでも写す。
    syncGarage();
  }
  function vehicleKey(v) {
    if (!v) return '';
    return [v.year, v.make, v.model, v.submodel || ''].join('|').toLowerCase();
  }
  function vehicleLabel(v) {
    if (!v) return '';
    var s = v.year + ' ' + v.make + ' ' + v.model;
    if (v.submodel) s += ' ' + v.submodel;
    return s;
  }
  function isSameVehicle(a, b) {
    if (!a || !b) return false;
    return vehicleKey(a) === vehicleKey(b);
  }
  /* ------- GA4 イベント (assets/custom-analytics.js) -------
     車両の登録はログインしていなくても localStorage で成立する。engage の DB に
     残るのはログイン顧客だけなので、GA 側は logged_in で区別できるようにする。
     未ロード・無効でも動作に影響しないよう存在チェックだけして呼ぶ。 */
  function track(name, vehicle, extra) {
    try {
      if (!window.ArkAnalytics || typeof window.ArkAnalytics.track !== 'function') return;
      var sync = window.ARK_GARAGE_SYNC || {};
      var params = {
        vehicle_year: vehicle ? Number(vehicle.year) : 0,
        vehicle_make: vehicle ? vehicle.make : '',
        vehicle_model: vehicle ? vehicle.model : '',
        vehicle_submodel: vehicle && vehicle.submodel ? vehicle.submodel : '',
        logged_in: sync.loggedIn ? 'yes' : 'no',
        garage_size: loadGarage().length,
      };
      if (extra) {
        Object.keys(extra).forEach(function (k) {
          params[k] = extra[k];
        });
      }
      window.ArkAnalytics.track(name, params);
    } catch (_) {}
  }

  function addToGarage(vehicle, maxVehicles) {
    var clean = sanitize(vehicle);
    if (!clean) return false;
    var garage = loadGarage();
    for (var i = 0; i < garage.length; i++) {
      if (isSameVehicle(garage[i], clean)) return false;
    }
    garage.unshift(clean);
    if (garage.length > maxVehicles) garage = garage.slice(0, maxVehicles);
    saveGarage(garage);
    track('vehicle_add', clean);
    return true;
  }
  function removeFromGarage(vehicle) {
    var garage = loadGarage().filter(function (v) {
      return !isSameVehicle(v, vehicle);
    });
    saveGarage(garage);
    track('vehicle_remove', sanitize(vehicle));
  }

  // ------- API --------
  function fetchApi(params, signal) {
    var enriched = Object.assign({}, params);
    if (SHOP_DOMAIN && !enriched.shop && API_BASE.indexOf('/apps/') === -1) {
      enriched.shop = SHOP_DOMAIN;
    }
    var qs = Object.keys(enriched)
      .map(function (k) {
        return encodeURIComponent(k) + '=' + encodeURIComponent(enriched[k]);
      })
      .join('&');
    return fetch(API_BASE + '?' + qs, {
      headers: { Accept: 'application/json' },
      signal: signal,
    }).then(function (res) {
      if (!res.ok) throw new Error('API ' + res.status);
      return res.json();
    });
  }

  // ------- DOM helpers --------
  function makeXSvg() {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.75');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    [
      { x1: '18', y1: '6', x2: '6', y2: '18' },
      { x1: '6', y1: '6', x2: '18', y2: '18' },
    ].forEach(function (attrs) {
      var line = document.createElementNS(SVG_NS, 'line');
      Object.keys(attrs).forEach(function (k) {
        line.setAttribute(k, attrs[k]);
      });
      svg.appendChild(line);
    });
    return svg;
  }

  function focusableIn(root) {
    return root.querySelectorAll(
      'button:not([disabled]):not([hidden]), [href], select:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])'
    );
  }

  // ------- widget --------
  function GarageWidget(container) {
    this.container = container;
    this.maxVehicles = parseInt(container.dataset.maxVehicles, 10) || 5;

    this.toggleBtn = container.querySelector('[data-fitment-garage-toggle]');
    this.modal = container.querySelector('[data-fitment-garage-modal]');
    this.backdrop = container.querySelector('[data-fitment-garage-backdrop]');
    this.closeBtn = container.querySelector('[data-fitment-garage-close]');
    this.list = container.querySelector('[data-fitment-garage-list]');
    this.savedSection = container.querySelector('[data-fitment-garage-saved]');
    this.capacity = container.querySelector('[data-fitment-garage-capacity]');
    this.addLabel = container.querySelector('[data-fitment-garage-add-label]');
    this.intro = container.querySelector('[data-fitment-garage-intro]');
    this.countBadge = container.querySelector('[data-fitment-garage-count]');
    this.title = container.querySelector('[data-fitment-garage-title]');
    this.resetBtn = container.querySelector('[data-fitment-garage-reset]');
    this.saveBtn = container.querySelector('[data-fitment-garage-save]');

    this.selects = {};
    var self = this;
    CASCADE_KEYS.forEach(function (key) {
      self.selects[key] = container.querySelector('[data-fitment-cascade="' + key + '"]');
    });

    this.cascadeLoaded = false;
    this._cascadeAbort = null;
    this._modalAbort = null; // for document-level listeners
    this._lastFocus = null;
    DBG('init widget', {
      position: container.dataset.position,
      maxVehicles: this.maxVehicles,
      savedCount: loadGarage().length,
      selected: loadSelected(),
    });
    this.bindEvents();
    this.render();
  }

  GarageWidget.prototype.bindEvents = function () {
    var self = this;
    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', function () {
        if (self.modal.hidden) self.open();
        else self.close();
      });
    }
    if (this.closeBtn)
      this.closeBtn.addEventListener('click', function () {
        self.close();
      });
    if (this.backdrop)
      this.backdrop.addEventListener('click', function () {
        self.close();
      });

    CASCADE_KEYS.forEach(function (key, idx) {
      var sel = self.selects[key];
      if (!sel) return;
      sel.addEventListener('change', function () {
        self.onCascadeChange(idx);
      });
    });

    if (this.resetBtn) {
      this.resetBtn.addEventListener('click', function () {
        self.resetCascade();
      });
    }
    if (this.saveBtn) {
      this.saveBtn.addEventListener('click', function () {
        self.saveCurrentSelection();
      });
    }

    // 外部からの vehicle 変更通知 → 表示更新
    window.addEventListener(VEHICLE_CHANGED_EVENT, function (e) {
      DBG('vehicle-changed event', e && e.detail);
      self.render();
    });
    window.addEventListener(VEHICLE_CHANGE_REQUEST_EVENT, function () {
      DBG('vehicle-change-request → open modal');
      self.open();
    });
  };

  GarageWidget.prototype.open = function () {
    DBG('open modal');
    this._lastFocus = document.activeElement;
    this.modal.hidden = false;
    if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');
    window.__arkScrollLock.lock();

    // modal scoped document listeners (Escape + focus trap)
    var self = this;
    this._modalAbort = new AbortController();
    var signal = this._modalAbort.signal;
    document.addEventListener(
      'keydown',
      function (e) {
        if (e.key === 'Escape' && !self.modal.hidden) {
          e.preventDefault();
          self.close();
          return;
        }
        if (e.key === 'Tab' && !self.modal.hidden) {
          var focusables = focusableIn(self.modal);
          if (focusables.length === 0) return;
          var first = focusables[0];
          var last = focusables[focusables.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      },
      { signal: signal }
    );

    if (!this.cascadeLoaded) this.loadYears();
    this.render();

    // 初期 focus
    var first = focusableIn(this.modal)[0];
    if (first) first.focus();
  };

  GarageWidget.prototype.close = function () {
    if (this.modal.hidden) return;
    DBG('close modal');
    this.modal.hidden = true;
    if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');
    window.__arkScrollLock.unlock();
    if (this._modalAbort) {
      this._modalAbort.abort();
      this._modalAbort = null;
    }
    // pending cascade fetch も abort
    if (this._cascadeAbort) {
      this._cascadeAbort.abort();
      this._cascadeAbort = null;
    }
    // return focus
    if (this._lastFocus && this._lastFocus.focus) {
      this._lastFocus.focus();
    } else if (this.toggleBtn) {
      this.toggleBtn.focus();
    }
  };

  GarageWidget.prototype.render = function () {
    var garage = loadGarage();
    var selected = loadSelected();
    var self = this;

    if (this.countBadge) {
      if (garage.length === 0) this.countBadge.hidden = true;
      else {
        this.countBadge.hidden = false;
        this.countBadge.textContent = String(garage.length);
      }
    }

    while (this.list.firstChild) this.list.removeChild(this.list.firstChild);
    if (garage.length === 0) {
      this.savedSection.hidden = true;
      if (this.title) this.title.textContent = this.title.dataset.empty || this.title.textContent;
      if (this.intro) this.intro.hidden = false;
      if (this.addLabel) this.addLabel.hidden = true;
    } else {
      this.savedSection.hidden = false;
      if (this.title)
        this.title.textContent = this.title.dataset.populated || this.title.textContent;
      if (this.intro) this.intro.hidden = true;
      if (this.addLabel) this.addLabel.hidden = false;

      garage.forEach(function (v) {
        var li = document.createElement('li');
        li.className = 'ark-garage__item';
        if (isSameVehicle(v, selected)) li.classList.add('ark-garage__item--active');

        var label = document.createElement('button');
        label.type = 'button';
        label.className = 'ark-garage__item-label';

        var radio = document.createElement('span');
        radio.className = 'ark-garage__item-radio';
        radio.setAttribute('aria-hidden', 'true');

        var text = document.createElement('span');
        text.textContent = vehicleLabel(v);

        label.appendChild(radio);
        label.appendChild(text);
        label.addEventListener('click', function () {
          setSelected(v);
          // 選択の切り替えだけを拾う。追加フローも setSelected を通るため、
          // setSelected 側ではなくこのクリックで送る (vehicle_add と二重にしない)。
          track('vehicle_select', v);
          self.close();
        });

        var trash = document.createElement('button');
        trash.type = 'button';
        trash.className = 'ark-garage__item-remove sk-ic';
        trash.setAttribute('aria-label', 'Remove');
        trash.appendChild(makeXSvg());
        trash.addEventListener('click', function (e) {
          e.stopPropagation();
          removeFromGarage(v);
          if (selected && isSameVehicle(v, selected)) {
            try {
              localStorage.removeItem(STORAGE_KEY);
            } catch (_) {}
            window.dispatchEvent(new CustomEvent(VEHICLE_CHANGED_EVENT, { detail: null }));
          }
          self.render();
        });

        li.appendChild(label);
        li.appendChild(trash);
        self.list.appendChild(li);
      });

      if (this.capacity) {
        this.capacity.textContent = garage.length + ' / ' + this.maxVehicles + ' saved';
      }
    }
  };

  // ------- cascade --------
  GarageWidget.prototype._abortCascade = function () {
    if (this._cascadeAbort) {
      this._cascadeAbort.abort();
    }
    this._cascadeAbort = new AbortController();
    return this._cascadeAbort.signal;
  };

  GarageWidget.prototype.loadYears = function () {
    var self = this;
    var signal = this._abortCascade();
    fetchApi({ action: 'years' }, signal)
      .then(function (years) {
        if (signal.aborted || !Array.isArray(years)) throw new Error('Invalid response');
        self.populate('year', years);
        self.selects.year.disabled = false;
        self.cascadeLoaded = true;
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        self.setPlaceholder('year', 'Error loading years');
      });
  };

  GarageWidget.prototype.onCascadeChange = function (idx) {
    var self = this;
    var values = this.getValues();
    for (var i = idx + 1; i < CASCADE_KEYS.length; i++) {
      this.resetSelect(CASCADE_KEYS[i]);
    }
    this.updateSaveButton();

    var signal = this._abortCascade();
    var onErr = function (err) {
      if (err && err.name === 'AbortError') return;
    };

    if (idx === 0 && values.year) {
      fetchApi({ action: 'makes', year: values.year }, signal)
        .then(function (makes) {
          if (signal.aborted) return;
          self.populate('make', makes);
          self.selects.make.disabled = false;
        })
        .catch(onErr);
    } else if (idx === 1 && values.make) {
      fetchApi({ action: 'models', year: values.year, make: values.make }, signal)
        .then(function (models) {
          if (signal.aborted) return;
          self.populate('model', models);
          self.selects.model.disabled = false;
        })
        .catch(onErr);
    } else if (idx === 2 && values.model) {
      fetchApi(
        {
          action: 'submodels',
          year: values.year,
          make: values.make,
          model: values.model,
        },
        signal
      )
        .then(function (submodels) {
          if (signal.aborted) return;
          if (submodels && submodels.length > 0) {
            self.populate('submodel', submodels);
            self.selects.submodel.disabled = false;
          } else {
            self.selects.submodel.disabled = true;
            self.setPlaceholder('submodel', '—');
          }
          self.updateSaveButton();
        })
        .catch(onErr);
    } else if (idx === 3) {
      this.updateSaveButton();
    }
  };

  GarageWidget.prototype.getValues = function () {
    var v = {};
    var self = this;
    CASCADE_KEYS.forEach(function (key) {
      v[key] = self.selects[key] ? self.selects[key].value : '';
    });
    return v;
  };

  GarageWidget.prototype.updateSaveButton = function () {
    var v = this.getValues();
    this.saveBtn.disabled = !(v.year && v.make && v.model);
  };

  GarageWidget.prototype.populate = function (key, items) {
    var sel = this.selects[key];
    if (!sel) return;
    while (sel.firstChild) sel.removeChild(sel.firstChild);
    var placeholder = document.createElement('option');
    placeholder.value = '';
    var ds = sel.dataset.placeholder;
    placeholder.textContent =
      ds || (key === 'year' ? 'Year' : key === 'submodel' ? 'Submodel (optional)' : '—');
    sel.appendChild(placeholder);
    items.forEach(function (item) {
      var opt = document.createElement('option');
      if (item && typeof item === 'object') {
        opt.value = item.value;
        opt.textContent = item.label != null ? item.label : item.value;
      } else {
        opt.value = item;
        opt.textContent = item;
      }
      sel.appendChild(opt);
    });
  };

  GarageWidget.prototype.resetSelect = function (key) {
    var sel = this.selects[key];
    if (!sel) return;
    sel.disabled = true;
    sel.value = '';
    while (sel.firstChild) sel.removeChild(sel.firstChild);
    var placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = sel.dataset.placeholder || '—';
    sel.appendChild(placeholder);
  };

  GarageWidget.prototype.setPlaceholder = function (key, text) {
    var sel = this.selects[key];
    if (!sel) return;
    while (sel.firstChild) sel.removeChild(sel.firstChild);
    var opt = document.createElement('option');
    opt.value = '';
    opt.textContent = text;
    sel.appendChild(opt);
  };

  GarageWidget.prototype.resetCascade = function () {
    var self = this;
    CASCADE_KEYS.forEach(function (key, idx) {
      if (idx === 0) {
        if (self.selects.year) self.selects.year.value = '';
      } else {
        self.resetSelect(key);
      }
    });
    this.updateSaveButton();
  };

  GarageWidget.prototype.saveCurrentSelection = function () {
    var v = this.getValues();
    var vehicle = {
      year: parseInt(v.year, 10),
      make: v.make,
      model: v.model,
      submodel: v.submodel || '',
    };
    DBG('saveCurrentSelection input', vehicle);
    if (!sanitize(vehicle)) {
      DBG('saveCurrentSelection rejected by sanitize', vehicle);
      return false;
    }
    var added = addToGarage(vehicle, this.maxVehicles);
    DBG('saveCurrentSelection added=' + added, vehicle);
    setSelected(vehicle);
    this.resetCascade();
    this.close();
    return added;
  };

  // ------- public API (window.ark.garage.*) -------
  // chat widget / 外部統合用。既存 widget と同じ storage / event を共有するため、
  // ここから書き込むと localStorage / cookie / vehicle-changed event 全てに伝播。
  // 全 mutating メソッドは sanitize() を経由する。
  var PUBLIC_MAX_VEHICLES_CAP = 50; // caller の maxVehicles はこれを超えない
  window.ark = window.ark || {};
  window.ark.garage = {
    list: function () {
      return loadGarage();
    },
    selected: function () {
      return loadSelected();
    },
    // add(vehicle, options)
    //   options.maxVehicles?: number  保存件数上限 (default 5, cap 50)
    //   options.select?: boolean      追加成功/重複問わず selected に設定 (default true)
    // 戻り値: { ok, added, vehicle } — added=false は重複でスキップしたケース
    add: function (vehicle, options) {
      var clean = sanitize(vehicle);
      if (!clean) return { ok: false, error: 'invalid vehicle' };
      var requested = (options && options.maxVehicles) || 5;
      var max = Math.max(1, Math.min(PUBLIC_MAX_VEHICLES_CAP, requested));
      var added = addToGarage(clean, max);
      if (!options || options.select !== false) setSelected(clean);
      return { ok: true, added: added, vehicle: clean };
    },
    select: function (vehicle) {
      var clean = sanitize(vehicle);
      if (!clean) return { ok: false, error: 'invalid vehicle' };
      setSelected(clean);
      return { ok: true, vehicle: clean };
    },
    remove: function (vehicle) {
      var clean = sanitize(vehicle);
      if (!clean) return { ok: false, error: 'invalid vehicle' };
      removeFromGarage(clean);
      return { ok: true };
    },
    clear: function () {
      saveGarage([]);
      try {
        localStorage.removeItem(STORAGE_KEY);
        setCookie(COOKIE_KEY, '', 0);
        window.dispatchEvent(new CustomEvent(VEHICLE_CHANGED_EVENT, { detail: null }));
      } catch (_) {}
      return { ok: true };
    },
    events: {
      VEHICLE_CHANGED: VEHICLE_CHANGED_EVENT,
      VEHICLE_CHANGE_REQUEST: VEHICLE_CHANGE_REQUEST_EVENT,
    },
  };

  // ------- init --------
  /* 同期を有効にした時点で既に入っている車両は、以後 1 度も操作されなければ永久に写らない。
     セッションで 1 度だけ現状を送って追いつかせる。毎ページ送ると無駄なので sessionStorage で
     抑える (書けない環境なら送らない = 変更時の同期だけに落ちる)。 */
  var INITIAL_SYNC_FLAG = 'ark.garageSynced';

  function initialSync() {
    if (!syncConfig()) return;
    try {
      if (sessionStorage.getItem(INITIAL_SYNC_FLAG)) return;
      sessionStorage.setItem(INITIAL_SYNC_FLAG, '1');
    } catch (_) {
      return;
    }
    if (loadGarage().length === 0) return; // 空を送っても消すものが無い
    pushGarage();
  }

  function init() {
    document.querySelectorAll('[data-fitment-garage]').forEach(function (el) {
      if (!el._arkGarageWidget) {
        el._arkGarageWidget = new GarageWidget(el);
      }
    });
    initialSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
